"use client";

import React from "react";
import { Loader, Mail, MessageSquare, Phone, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="230948" className="flex flex-col items-center gap-4 rounded-lg border p-8 text-center" style={{
      borderColor: "var(--primary)",
      background: "var(--accent)"
    }}>
        <div className="flex size-14 items-center justify-center rounded-full" style={{
        background: "var(--primary)"
      }}>
          <Mail className="size-7" style={{
          color: "var(--primary-foreground)"
        }} />
        </div>
        <p className="font-default text-xl font-semibold" style={{
        color: "var(--foreground)"
      }}>
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2 rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default flex items-center gap-2 text-sm font-medium" style={{
        color: "var(--foreground)"
      }}>
              <User className="size-3.5" style={{
          color: "var(--primary)"
        }} />
              Full Name
            </FormLabel>
            <FormControl>
              <Input type="text" placeholder="Your name" className="h-11 rounded-none border-0 border-b px-0 transition-colors focus-visible:border-primary focus-visible:ring-0" style={{
          background: "transparent",
          borderColor: "var(--border)",
          color: "var(--foreground)"
        }} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default flex items-center gap-2 text-sm font-medium" style={{
        color: "var(--foreground)"
      }}>
              <Mail className="size-3.5" style={{
          color: "var(--primary)"
        }} />
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="hello@example.com" className="h-11 rounded-none border-0 border-b px-0 transition-colors focus-visible:border-primary focus-visible:ring-0" style={{
          background: "transparent",
          borderColor: "var(--border)",
          color: "var(--foreground)"
        }} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{}} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default flex items-center gap-2 text-sm font-medium" style={{
        color: "var(--foreground)"
      }}>
              <Phone className="size-3.5" style={{
          color: "var(--primary)"
        }} />
              Phone Number
              <span className="text-xs font-normal" style={{
          color: "var(--muted-foreground)"
        }}>(optional)</span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" className="h-11 rounded-none border-0 border-b px-0 transition-colors focus-visible:border-primary focus-visible:ring-0" style={{
          background: "transparent",
          borderColor: "var(--border)",
          color: "var(--foreground)"
        }} {...field} />
            </FormControl>
            <FormDescription style={{
        color: "var(--muted-foreground)"
      }}>
              We only use this to discuss your smart home vision.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default flex items-center gap-2 text-sm font-medium" style={{
        color: "var(--foreground)"
      }}>
              <MessageSquare className="size-3.5" style={{
          color: "var(--primary)"
        }} />
              Your Vision
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us how we can help you simplify your home..." className="min-h-32 rounded-none border-0 border-b px-0 py-3 transition-colors focus-visible:border-primary focus-visible:ring-0" style={{
          background: "transparent",
          borderColor: "var(--border)",
          color: "var(--foreground)"
        }} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border p-4" style={{
      borderColor: "var(--destructive)",
      background: "var(--destructive)/10"
    }}>
          <p className="text-sm" style={{
        color: "var(--destructive)"
      }}>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full font-default text-base font-semibold tracking-wide rounded-full shadow-lg transition-transform hover:scale-[1.02]" style={{
      background: "var(--primary)",
      color: "var(--primary-foreground)"
    }}>
        {isSubmitting ? <>
            <Loader className="animate-spin size-5" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function KontaktEjerneDirekte() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="lets-connect" ref={ref} className="bg-background text-foreground py-20 px-4">
      <div className={`mx-auto w-full max-w-[600px] transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="mb-10 text-center">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full shadow-sm" style={{
          background: "var(--accent)"
        }}>
            <Mail className="size-7" style={{
            color: "var(--primary)"
          }} />
          </div>
          <h2 className="font-default mb-3 text-3xl font-bold tracking-tight sm:text-4xl" style={{
          color: "var(--foreground)"
        }}>
            How can we help you simplify?
          </h2>
          <p className="font-default text-base leading-relaxed" style={{
          color: "var(--muted-foreground)"
        }}>
            Whether you are just starting with HomeKit or looking to refine your existing setup, we are here to help you find digital zen.
          </p>
        </div>

        {}
        <div className="rounded-4xl border-none shadow-xl p-8 sm:p-10" style={{
        background: "var(--card)"
      }}>
          <FormProvider formKey="contact_form_get_in_touch" formId="contact_form_get_in_touch" sectionName="lets-connect" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Send Message" successMessage="Thank you. We will reach out shortly to help you simplify your living space." errorMessage="Something went wrong. Please try again or email us directly.">
            <ContactFormInner />
          </FormProvider>
        </div>

        {}
        <div className="mt-8 flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-8">
          <Link to="#" className="font-default flex items-center gap-2 text-sm transition-opacity hover:opacity-80" style={{
          color: "var(--muted-foreground)"
        }}>
            <Phone className="size-4" style={{
            color: "var(--primary)"
          }} />
            Schedule a Call
          </Link>
          <Link to="mailto:hello@mozacasa.com" className="font-default flex items-center gap-2 text-sm transition-opacity hover:opacity-80" style={{
          color: "var(--muted-foreground)"
        }}>
            <Mail className="size-4" style={{
            color: "var(--primary)"
          }} />
            hello@mozacasa.com
          </Link>
        </div>
      </div>
    </section>;
}